/**
 * Canvas-based image crop utility
 * Takes a source image URL and a crop area, returns a cropped Blob
 */

export interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Create a cropped image from a source URL and crop area.
 * Uses an offscreen canvas to draw only the cropped region.
 *
 * @param imageSrc - The source image URL
 * @param cropArea - The pixel-based crop area { x, y, width, height }
 * @param outputType - MIME type for the output ('image/jpeg' | 'image/webp' | 'image/png')
 * @param quality - Quality for JPEG/WebP output (0-1)
 * @returns A Blob of the cropped image
 */
export async function getCroppedImage(
  imageSrc: string,
  cropArea: CropArea,
  outputType = 'image/jpeg',
  quality = 0.92
): Promise<Blob> {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  // Set canvas size to the crop area
  canvas.width = cropArea.width
  canvas.height = cropArea.height

  // Draw the cropped region
  ctx.drawImage(image, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, cropArea.width, cropArea.height)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      },
      outputType,
      quality
    )
  })
}

/**
 * Load an image from a URL and return an HTMLImageElement
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = err => reject(err)
    img.src = src
  })
}
